import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Button from 'components/gruenland/atoms/button/Button';
import PasswordInput from 'components/gruenland/atoms/input-password';
import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { resolveReferenceTarget } from 'helpers/reference';
import { ResetPasswordProps } from '.';
import { InputProps } from 'components/gruenland/atoms/input';

const ResetPasswordForm: React.FC<ResetPasswordProps> = ({ resetPassword }) => {
  //i18n messages
  const { formatMessage } = useFormat({ name: 'gruenland' });

  //next/navigation
  const router = useRouter();

  //register data
  const [data, setData] = useState({ email: '', password: '', confirmPassword: '' });

  //error
  const [error, setError] = useState('');

  //processing...
  const [loading, setLoading] = useState(false);

  //handle text input change
  const handleChange: InputProps['onChange'] = ({ target: { name, value } }) => {
    setData({ ...data, [name]: value });
  };

  //data validation
  const validate = () => {
    //validation schema
    const passwordsMatch = data.password === data.confirmPassword;

    //UI error messages
    if (!passwordsMatch)
      setError(formatMessage({ id: 'validation.confirmPassword', defaultMessage: 'Le password non corrispondono.' }));

    //return a boolean representing the data validity
    return passwordsMatch;
  };

  //form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    //validate data
    if (!validate()) return;
    //processing starts
    setLoading(true);
    //try registering the user with given credentials
    try {
      const response = await resetPassword(data.password);
      if (!response.accountId) {
        setError(
          formatMessage({
            id: 'error.resetPassword',
            defaultMessage: 'Errore durante il reset della password. Riprova più tardi.',
          }),
        );
      } else {
        setError('');
        router?.push(resolveReferenceTarget({ type: 'link', link: '/account' }) as string);
      }
    } catch (err) {
      setError(
        formatMessage({ id: 'feedback.wentWrong', defaultMessage: 'Qualcosa è andato storto. Riprova più tardi.' }),
      );
    }
    //processing ends
    setLoading(false);
  };
  return (
    <>
      <Typography as="h3" className="mb-4 text-[16px] md:mb-6 md:text-[20px] lg:text-[24px]">
        {formatMessage({ id: 'passwordreset.title', defaultMessage: 'Reset password' })}
      </Typography>

      <form onSubmit={handleSubmit}>
        {error && <Typography className="mb-3 text-[12px] capitalize text-accent-red">{error}</Typography>}

        <PasswordInput
          required
          id="password"
          name="password"
          autoComplete="current-password"
          placeholder={formatMessage({ id: 'user.password', defaultMessage: 'Password' })}
          className="mb-4 md:mb-5"
          onChange={handleChange}
        />

        <PasswordInput
          required
          id="confirm-password"
          name="confirmPassword"
          autoComplete="current-password"
          placeholder={formatMessage({ id: 'user.confirmPassword', defaultMessage: 'Conferma password' })}
          className="mb-4 md:mb-5"
          onChange={handleChange}
        />

        <Button size="full" type="submit" className="mb-4 text-[16px] leading-tight md:mb-5" disabled={loading}>
          {formatMessage({ id: 'passwordreset.title', defaultMessage: 'Reset password' })}
        </Button>

        <Link variant="menu-item" className="mx-auto block w-fit text-[14px]" link={{ type: 'link', link: '/login' }}>
          {formatMessage({ id: 'passwordreset.cta', defaultMessage: 'Torna al login' })}
        </Link>
      </form>
    </>
  );
};

export default ResetPasswordForm;
